import AssessmentIcon from '@mui/icons-material/Assessment';
import { activeRiskReductionUntil, riskReductionPercent } from '../linkedin-accounts/risk-reduction';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import { useQueryClient } from '@tanstack/react-query';
import { addFunnelToGlobalStore } from 'API/global-funnels';
import {
  changeFunnelStatus,
  cloneFunnel,
  deleteFunnel,
  getAllFunnels,
  getToBeApprovedMessages,
  startFunnel,
  updateFunnel,
  updateToBeApprovedMessages,
} from 'API/linkedin-funnel';
import { updateContactIsActive } from 'API/linkedin-funnel-search';
import { ErrorMessages, ProductGroups, Products, Routes, ZoomLevels } from 'enums';
import { CampaignFilterTypes, FunnelStatuses, FunnelTypes, MessageSender, MessageToApprove } from 'features/co-pilot/co-pilot.types';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiSuccessAlert from 'features/ui/base-components/pai-success-alert';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import { useHasLicense } from 'hooks/use-has-license';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiTextField from '../../ui/base-components/pai-text-field';
import { CampaignBuilderSteps } from '../campaign-builder/campaign-builder.types';
import ContactDrawer from '../campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-drawer';
import useContactDrawer from '../campaign-builder/use-contact-drawer';
import { useLinkedinAccountsQuery } from '../linkedin-accounts/hooks';
import getLocalizedText from '../translationFileProduct';
import ApproveContactsDialog from './approve-contacts-dialog';
import ApproveMessagesDialog from './approve-messages-dialog';
import CampaignDetailPage from './campaign-detail-page';
import CampaignItem from './campaign-item';
import CampaignKanban from './campaign-kanban';
import ScheduleStartDialog from './schedule-start-dialog';
import { cancelCampaignSchedule } from '../../../API/campaign-schedule';
import { FunnelOverview, StatisticsAccordionStates } from './campaign-overview.types';
import CampaignStatusDescriptionsDialog from './campaign-status-descriptions-dialog';
import CloneCampaignDialog from './clone-campaign-dialog';
import { clonesInWizard, editsInWizard } from './wizard-routing';
import { createWizardCampaign } from '../campaign-wizard/campaign-wizard.api';
import ConfirmDeclineMessageDialog from './confirm-decline-message-dialog';
import DeleteCampaignDialog from './delete-campaign-dialog';
import EditCampaignLinkedinAccountDialog from './edit-campaign-linkedin-account-dialog';
import RenameCampaignDialog from './rename-campaign-dialog';
import WorkUnitsStatusModal from './work-units-status-modal';

interface Props {
  productGroup: ProductGroups;

  autoSizePanel(newIdx: number): void;

  fetchFinalContacts(newPage: number | undefined, newSearchParam: string | undefined, newCampaignId: string | undefined): void;

  resetCampaignBuilder(): void;

  handleResetCampaign(): void;

  handleOpenCopilotLicenseExpiredDialog(): void;

  handleOpenCreateCampaignDialog(): void;
}

const CampaignOverview: React.FC<Props> = ({
  productGroup,
  autoSizePanel,
  fetchFinalContacts,
  resetCampaignBuilder,
  handleResetCampaign,
  handleOpenCopilotLicenseExpiredDialog,
  handleOpenCreateCampaignDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { role } = useUserData();
  const productLicensed = useHasLicense(
    productGroup === ProductGroups.HR_BOOSTER ? Products.SAIA_RECRUITING : Products.SAIA_SALES
  );

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const routerLocation = useLocation();
  /** Vom Wizard übergeben: die eben gestartete Kampagne wird mit „NEU“ markiert. */
  const newCampaignId = (routerLocation.state as { newCampaignId?: string } | null)?.newCampaignId;
  /** The campaign BUILDER. It takes its campaign from `?_id=`, never from a path segment. */
  const campaignRoute =
    productGroup === ProductGroups.HR_BOOSTER ? Routes.RECRUITING_BOOSTER_CO_PILOT_CAMPAIGN : Routes.LEAD_BOOSTER_CO_PILOT_CAMPAIGN;

  /** Set when the URL is `.../projects/:campaignId` — then the detail page replaces the list. */
  const { campaignId: routeCampaignId } = useParams();
  /**
   * The campaign LIST, and the base for the detail URL.
   *
   * This used to point at `campaignRoute`, so "Details" navigated to
   * `.../campaign/<id>` — the builder. The builder found no `?_id=`, concluded there was
   * nothing to load, and started a new campaign. Reported as: "Wenn ich da drauf drücke,
   * dann will SAIA eine neue Kampagne erstellen."
   */
  const campaignListPath =
    productGroup === ProductGroups.HR_BOOSTER ? Routes.RECRUITING_BOOSTER_CO_PILOT_PROJECTS : Routes.LEAD_BOOSTER_CO_PILOT_PROJECTS;

  const [searchParams] = useSearchParams();
  const currentActiveFunnelId = searchParams.get('_id');

  const [funnels, setFunnels] = useState<FunnelOverview[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'board'>(() => (localStorage.getItem('campaignViewMode') === 'board' ? 'board' : 'list'));
  const [searchParam, setSearchParam] = useState('');
  const [linkedinAccountFilter, setLinkedinAccountFilter] = useState<string[]>([]);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState<string[]>([]);
  const [fetchFunnelsLoading, setFetchFunnelsLoading] = useState(false);

  const [rememberLinkedinAccount, setRememberLinkedinAccount] = useState(false);
  const isAdminLogin = !_.isNil(localStorage.getItem('adminEmail'));

  const [startFunnelLoading, setStartFunnelLoading] = useState(false);

  const itemsPerPage = 25;
  const [itemCount, setItemCount] = useState(0);
  const [page, setPage] = useState<number>(1);

  /** Display-only: how many of the currently loaded campaigns are actively running. */
  const activeCampaignCount = funnels.filter((funnel) => funnel.status === FunnelStatuses.STARTED).length;

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState<{ open: boolean; loading: boolean; funnel: FunnelOverview | null }>({
    open: false,
    loading: false,
    funnel: null,
  });

  const [cloneCampaignDialog, setCloneCampaignDialog] = useState<{
    open: boolean;
    loading: boolean;
    nameTakenError: boolean;
    funnel: FunnelOverview | null;
    funnelName: string;
    selectedLinkedinAccount: string;
    cloneContacts: boolean;
  }>({
    open: false,
    loading: false,
    nameTakenError: false,
    funnel: null,
    funnelName: '',
    selectedLinkedinAccount: '',
    cloneContacts: false,
  });
  const {
    data: linkedinAccounts = [],
    error: linkedinAccountsError,
  } = useLinkedinAccountsQuery();

  const [scheduleDialog, setScheduleDialog] = useState<{ open: boolean; funnel: FunnelOverview | null }>({
    open: false,
    funnel: null,
  });

  const [renameCampaignDialog, setRenameCampaignDialog] = useState<{
    open: boolean;
    funnel: FunnelOverview | null;
    campaignName: string;
    loading: boolean;
    nameTakenError: boolean;
  }>({
    open: false,
    funnel: null,
    campaignName: '',
    loading: false,
    nameTakenError: false,
  });

  /** Kampagne, deren wartende Kontakte gerade freigegeben werden. */
  const [approveContactsFunnelId, setApproveContactsFunnelId] = useState<string | null>(null);
  const [approveMessagesDialog, setApproveMessagesDialog] = useState<{
    open: boolean;
    loading: boolean;
    funnelId: string | null;
    messages: MessageToApprove[];
    messageIndex: number;
    messageSender: MessageSender | null;
    messageToEditText: string | null;
    messageToEditIndex: number | null;
  }>({
    open: false,
    loading: false,
    funnelId: null,
    messages: [],
    messageIndex: 0,
    messageSender: null,
    messageToEditText: null,
    messageToEditIndex: null,
  });
  const [confirmDeclineMessageDialog, setConfirmDeclineMessageDialog] = useState<{ open: boolean; loading: boolean }>({
    open: false,
    loading: false,
  });

  const [editCampaignLinkedinAccountDialog, setEditCampaignLinkedinAccountDialog] = useState<{
    open: boolean;
    loading: boolean;
    funnel: FunnelOverview | null;
    selectedLinkedinAccount: string;
  }>({ open: false, loading: false, funnel: null, selectedLinkedinAccount: '' });

  const [campaignStatusDescriptionsDialog, setCampaignStatusDescriptionsDialog] = useState<{ open: boolean }>({ open: false });

  const [workUnitsStatusModal, setWorkUnitsStatusModal] = useState<{ open: boolean }>({ open: false });

  const [approveMessageSuccess, setApproveMessageSuccess] = useState(false);
  const [allMessagesProcessed, setAllMessagesProcessed] = useState(false);

  const { contactDrawer, handleOpenContactDrawer, handleUpdateDrawerContactIsActive, handleCloseContactDrawer } = useContactDrawer();

  const funnelStatusOptions = _.map(FunnelStatuses, (status) => ({
    label: t(_.camelCase(status)),
    value: status,
  }));

  const linkedinAccountOptions = _.map(linkedinAccounts, (account) => ({
    label: !_.isNil(account.firstName) && !_.isNil(account.lastName) ? `${account.firstName} ${account.lastName}` : account.userEmail,
    value: account._id,
  }));

  let typingTimer: ReturnType<typeof setTimeout>;
  const type =
    productGroup === ProductGroups.HR_BOOSTER
      ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT
      : productGroup === ProductGroups.LEAD_BOOSTER
      ? FunnelTypes.SOCIAL_SELLING_CO_PILOT
      : undefined;

  const [saveGlobalFunnelDialog, setSaveGlobalFunnelDialog] = useState<{
    open: boolean;
    funnel: FunnelOverview | null;
  }>({ open: false, funnel: null });
  const [globalFunnelForm, setGlobalFunnelForm] = useState({ description: '', conversionRate: '', creatorName: '' });
  const [globalFunnelLoading, setGlobalFunnelLoading] = useState(false);
  const [globalFunnelSuccess, setGlobalFunnelSuccess] = useState(false);
  const [globalFunnelError, setGlobalFunnelError] = useState('');

  const fetchFunnels = async (
    newPage: number = page,
    newSearchParam: string = searchParam,
    newLinkedinAccountFilter: string[] = linkedinAccountFilter,
    newCampaignStatusFilter: string[] = campaignStatusFilter
  ) => {
    setFetchFunnelsLoading(true);

    const res = await getAllFunnels(
      type,
      newSearchParam,
      newPage - 1,
      itemsPerPage,
      CampaignFilterTypes.FUNNEL_NAME,
      newLinkedinAccountFilter,
      newCampaignStatusFilter
    );
    if (res && !res.error) {
      const resFunnels = res.campaigns;
      if (_.isEmpty(resFunnels) && newSearchParam !== '') {
        setFunnels([]);
        setItemCount(0);
        setPage(1);
      } else {
        setFunnels(resFunnels);
        setItemCount(res.total);
        setPage(newPage);
      }

      const startedFunnelId = localStorage.getItem('startedFunnelId');
      const isStartedFunnelInResFunnels = !_.isNil(startedFunnelId) && _.some(resFunnels, (funnel) => funnel._id === startedFunnelId);

      if (isStartedFunnelInResFunnels) {
        setSelectedCampaignId(startedFunnelId);
      } else {
        setSelectedCampaignId(null);
      }

      if (startedFunnelId) {
        localStorage.removeItem('startedFunnelId');
      }
    } else {
      setFunnels([]);
      setItemCount(0);
      setPage(1);
      handleOpenErrorDialog(res.error, '/campaigns', 'get');
    }

    setFetchFunnelsLoading(false);
  };

  useEffect(() => {
    const savedLinkedinAccountFilter = localStorage.getItem('rememberLinkedinAccount');

    if (!isAdminLogin && !_.isNil(savedLinkedinAccountFilter)) {
      const savedLinkedinAccountFilterArray = JSON.parse(savedLinkedinAccountFilter);
      if (!_.isEmpty(savedLinkedinAccountFilterArray)) {
        setLinkedinAccountFilter(savedLinkedinAccountFilterArray);
        setRememberLinkedinAccount(true);
        fetchFunnels(1, undefined, savedLinkedinAccountFilterArray);
      }
    } else {
      fetchFunnels();
    }
  }, [itemsPerPage]);

  useEffect(() => {
    if (!_.isNil(linkedinAccountsError)) {
      handleOpenErrorDialog(linkedinAccountsError, '/linkedinAccounts/getAllLinkedInAccounts', 'get');
    }
  }, [linkedinAccountsError, handleOpenErrorDialog]);

  /**
   * Search handlers
   */

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchFunnels = async () => {
    fetchFunnels(1, searchParam);
  };

  const searchKeyUp = () => {
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      handleSearchFunnels();
    }, 500);
  };

  const searchKeyDown = () => {
    clearTimeout(typingTimer);
  };

  const handleLinkedinAccountFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    setLinkedinAccountFilter(newValue);

    fetchFunnels(1, undefined, newValue);

    if (!isAdminLogin) {
      resetRememberLinkedinAccount();
    }
  };

  const handleCampaignStatusFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;
    setCampaignStatusFilter(newValue);
    fetchFunnels(1, undefined, undefined, newValue);
  };

  /**
   * Remember LinkedIn account handlers
   */

  const handleRememberLinkedinAccount = () => {
    const newRememberLinkedinAccount = !rememberLinkedinAccount;
    if (newRememberLinkedinAccount) {
      localStorage.setItem('rememberLinkedinAccount', JSON.stringify(linkedinAccountFilter));
    } else {
      localStorage.removeItem('rememberLinkedinAccount');
    }
    setRememberLinkedinAccount(newRememberLinkedinAccount);
  };

  const resetRememberLinkedinAccount = () => {
    setRememberLinkedinAccount(false);
    localStorage.removeItem('rememberLinkedinAccount');
  };

  /**
   * Pagination handlers
   */

  const handleChangePage = (event: unknown, newPage: number) => {
    fetchFunnels(newPage);
  };

  /**
   * Misc handlers
   */

  const handleNewFunnelClick = () => {
    resetCampaignBuilder();
    handleOpenCreateCampaignDialog();
  };

  /**
   * Delete funnel dialog handlers
   */

  const handleOpenConfirmDeleteDialog = (funnel: FunnelOverview) => {
    return () => {
      setConfirmDeleteDialog((prev) => ({ ...prev, open: true, funnel }));
    };
  };

  const handleCloseConfirmDeleteDialog = () => {
    setConfirmDeleteDialog({ open: false, loading: false, funnel: null });
  };

  const handleDeleteFunnel = (funnelId: string) => {
    return async () => {
      setConfirmDeleteDialog((prev) => ({ ...prev, loading: true }));

      const res = await deleteFunnel(funnelId);

      if (res && !res.error) {
        const newPage = funnels?.length === 1 && page > 1 ? page - 1 : page;
        fetchFunnels(newPage);
        handleCloseConfirmDeleteDialog();

        if (!_.isNil(currentActiveFunnelId)) {
          if (currentActiveFunnelId === funnelId) {
            resetCampaignBuilder();
          }
        }
      } else {
        setConfirmDeleteDialog((prev) => ({ ...prev, loading: false }));
        const error = res?.error;
        if (error?.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || error?.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
          handleOpenCopilotLicenseExpiredDialog();
        } else {
          handleOpenErrorDialog(error || { code: 'UNKNOWN_ERROR', message: t('unknownError') }, '/campaigns/:id', 'delete');
        }
      }
    };
  };

  /**
   * Stop, restart and archive funnel handlers
   */

  const handleStopFunnel = (funnelId: string) => {
    return async () => {
      const res = await changeFunnelStatus(funnelId, FunnelStatuses.SUSPENDED);

      if (!res.error) {
        fetchFunnels();
      } else {
        if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
          handleOpenCopilotLicenseExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/campaigns/:id/status', 'post');
        }
      }
    };
  };

  const handleAcceptStartFunnel = (funnelId: string) => {
    return async () => {
      setStartFunnelLoading(true);

      const res = await startFunnel(funnelId);

      if (res.error) {
        if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
          handleOpenCopilotLicenseExpiredDialog();
        } else {
          handleOpenErrorDialog(res?.error, '/campaigns/:id/status', 'post');
        }
      }

      setStartFunnelLoading(false);
    };
  };

  const handleRestartFunnel = (funnelId: string) => {
    return async () => {
      const res = await changeFunnelStatus(funnelId, FunnelStatuses.STARTED);

      if (!res.error) {
        handleAcceptStartFunnel(funnelId)();

        if (currentActiveFunnelId === funnelId) {
          handleResetCampaign();
        }

        fetchFunnels();
      } else {
        if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
          handleOpenCopilotLicenseExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/campaigns/:id/status', 'post');
        }
      }
    };
  };

  const handleArchiveCampaign = (funnel: FunnelOverview, archive: boolean) => {
    return async () => {
      const funnelId = funnel._id;
      const res = await changeFunnelStatus(
        funnelId,
        archive
          ? FunnelStatuses.ARCHIVED
          : funnel.isCompleted
          ? FunnelStatuses.FINISHED
          : !_.isNil(funnel.workedLastFor)
          ? FunnelStatuses.SUSPENDED
          : FunnelStatuses.CREATED
      );

      if (!res.error) {
        fetchFunnels();
      } else {
        if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
          handleOpenCopilotLicenseExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/campaigns/:id/status', 'post');
        }
      }
    };
  };

  /**
   * Clone campaign dialog handlers
   */

  const handleOpenCloneCampaignDialog = (newFunnel: FunnelOverview) => {
    return () => {
      setCloneCampaignDialog({ ...cloneCampaignDialog, open: true, funnel: newFunnel, selectedLinkedinAccount: newFunnel.linkedinAccount || '' });
    };
  };

  const handleCloseCloneCampaignDialog = () => {
    setCloneCampaignDialog({
      open: false,
      loading: false,
      nameTakenError: false,
      funnel: null,
      funnelName: '',
      selectedLinkedinAccount: '',
      cloneContacts: false,
    });
  };

  const handleCloneCampaignNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCloneCampaignDialog((prev) => ({ ...prev, funnelName: event.target.value }));
  };

  const handleLinkedinAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCloneCampaignDialog((prev) => ({ ...prev, selectedLinkedinAccount: event.target.value }));
  };

  const handleCloneContactsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCloneCampaignDialog((prev) => ({ ...prev, cloneContacts: event.target.checked }));
  };

  const handleCloneCampaign = async () => {
    const { funnel, funnelName, selectedLinkedinAccount, cloneContacts } = cloneCampaignDialog;

    if (_.isNil(funnel)) return;

    setCloneCampaignDialog((prev) => ({ ...prev, loading: true }));

    // Recruiting-Wizard-Kampagne: Klon im Assistenten (eigene lokale Blacklists, Bewertung der übernommenen
    // Kontakte), nicht über den alten Klon mit anschließendem Builder.
    if (clonesInWizard(productGroup === ProductGroups.HR_BOOSTER, funnel)) {
      try {
        const created = await createWizardCampaign({
          name: funnelName.trim(),
          accountId: selectedLinkedinAccount || funnel.linkedinAccount,
          briefing: '',
          cloneFromCampaignId: funnel._id,
          cloneContacts,
        });
        handleCloseCloneCampaignDialog();
        navigate(`${Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN}/${created.campaignId}`);
      } catch (e) {
        const err = e as { nameTaken?: boolean; message?: string; raw?: unknown };
        setCloneCampaignDialog((prev) => ({ ...prev, loading: false, ...(err.nameTaken ? { nameTakenError: true } : {}) }));
        if (!err.nameTaken) handleOpenErrorDialog((err.raw ?? { message: err.message }) as never, '/campaign-wizard/campaigns', 'post');
      }
      return;
    }

    const res = await cloneFunnel(funnel._id, funnelName, cloneContacts);

    if (!res.error) {
      const newFunnel: FunnelOverview = res;

      // Derselbe Einstieg wie beim Bearbeiten (`handleEditFunnel`): Ohne `stepIndex` fällt der
      // Builder auf Schritt 0 zurück und überspringt sein Init (Panel-Größe, Kontakte).
      const handleCloneFunnelSuccess = () => {
        const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);
        const selectedIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY);

        resetCampaignBuilder();
        navigate({
          pathname: campaignRoute,
          search: `?_id=${newFunnel._id}&stepIndex=${selectedIndex}`,
        });
        autoSizePanel(selectedIndex);
        fetchFinalContacts(undefined, undefined, newFunnel._id);

        handleCloseCloneCampaignDialog();
      };

      if (funnel.linkedinAccount !== selectedLinkedinAccount) {
        const updateRes = await updateFunnel(newFunnel._id, { linkedinAccount: selectedLinkedinAccount });

        if (!updateRes.error) {
          handleCloneFunnelSuccess();
        } else {
          setCloneCampaignDialog((prev) => ({ ...prev, loading: false }));
          handleOpenErrorDialog(updateRes?.error, '/campaigns/:id', 'patch');
        }
      } else {
        handleCloneFunnelSuccess();
      }
    } else {
      if (res.error.message?.includes('exists')) {
        setCloneCampaignDialog((prev) => ({ ...prev, loading: false, nameTakenError: true }));
      } else if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
        setCloneCampaignDialog((prev) => ({ ...prev, loading: false }));
        handleOpenCopilotLicenseExpiredDialog();
      } else {
        setCloneCampaignDialog((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res?.error, '/campaigns/:id/clone', 'post');
      }
    }
  };

  /**
   * Rename campaign dialog handlers
   */

  const handleOpenRenameCampaignDialog = (newFunnel: FunnelOverview) => {
    return () => {
      setRenameCampaignDialog((prev) => ({ ...prev, open: true, funnel: newFunnel, campaignName: newFunnel.name }));
    };
  };

  const handleCloseRenameCampaignDialog = () => {
    setRenameCampaignDialog({ open: false, loading: false, funnel: null, campaignName: '', nameTakenError: false });
  };

  const handleCampaignNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRenameCampaignDialog((prev) => ({ ...prev, campaignName: event.target.value }));
  };

  const handleRenameCampaign = async () => {
    const { funnel, campaignName } = renameCampaignDialog;

    if (_.isNil(funnel)) return;

    setRenameCampaignDialog((prev) => ({ ...prev, loading: true }));

    const res = await updateFunnel(funnel._id, { name: campaignName });

    if (!res.error) {
      setFunnels((prevFunnels) => prevFunnels.map((item) => (item._id === funnel._id ? { ...item, name: campaignName } : item)));
      handleCloseRenameCampaignDialog();
    } else {
      if (res.error.message?.includes('exists')) {
        setRenameCampaignDialog((prev) => ({ ...prev, loading: false, nameTakenError: true }));
      } else {
        setRenameCampaignDialog((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res?.error, '/campaigns/:id', 'patch');
      }
    }
  };

  /**
   * Approve messages dialog handlers
   */

  const handleOpenApproveContactsDialog = (funnelId: string) => () => setApproveContactsFunnelId(funnelId);

  const handleContactApprovalRemainingChange = (funnelId: string, remaining: number) => {
    if (remaining < 0) return;
    setFunnels((prevFunnels) => prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedContacts: remaining } : funnel)));
  };

  const handleOpenApproveMessagesDialog = (funnelId: string) => {
    return async () => {
      setApproveMessagesDialog((prev) => ({ ...prev, open: true, loading: true, funnelId }));

      const res = await getToBeApprovedMessages(funnelId);

      if (!res.error) {
        const resMessages: MessageToApprove[] = res.messages || [];
        const messagesWithoutApproved = resMessages.map((msg) => {
          if (!_.isNil(msg.approved)) {
            delete msg.approved;
          }
          return msg;
        });
        setApproveMessagesDialog((prev) => ({ ...prev, loading: false, messages: messagesWithoutApproved, messageSender: res.sender || null }));
      } else {
        setApproveMessagesDialog((prev) => ({ ...prev, open: false, loading: false }));
        handleOpenErrorDialog(res.error, '/messages/campaign/:id/pending', 'get');
      }
    };
  };

  const handleRemoveContact = (linkedInURL: string, idx: number) => {
    return async () => {
      const { messages, funnelId } = approveMessagesDialog;

      if (!_.isNil(funnelId)) {
        const updateMessages = [...messages];

        const messageToApprove = updateMessages[idx];
        messageToApprove.approved = false;

        setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));

        const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

        const [resUpdateMessages, resRemoveContact] = await Promise.all([
          updateToBeApprovedMessages(funnelId, [messageToApprove]),
          updateContactIsActive(funnelId, [linkedInURL], false),
        ]);

        if (!resUpdateMessages.error) {
          setApproveMessagesDialog((prev) => ({
            ...prev,
            loading: false,
            messageIndex: 0,
            messages: prev.messages.filter((message) => _.isNil(message.approved)),
          }));
          if (messagesToApprove.length === 0) {
            setFunnels((prevFunnels) => prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: 0 } : funnel)));
            handleCloseApproveMessagesDialog();
            handleOpenAllMessagesProcessedSnackbar();
          } else {
            setFunnels((prevFunnels) =>
              prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: funnel.toBeApprovedMessages - 1 } : funnel))
            );
          }
        } else {
          setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
          handleOpenErrorDialog(resUpdateMessages.error, '/messages/bulk-approve', 'post');
        }

        if (resRemoveContact.error) {
          handleOpenErrorDialog(resRemoveContact.error, '/campaigns/:id/contacts', 'put');
        }
      }
    };
  };

  const handleNextMessage = () => {
    const { messages } = approveMessagesDialog;
    setApproveMessagesDialog((prev) => ({
      ...prev,
      messageIndex: prev.messageIndex === messages.length - 1 ? 0 : prev.messageIndex + 1,
      messageToEditText: null,
      messageToEditIndex: null,
    }));
  };

  const handlePrevMessage = () => {
    const { messages } = approveMessagesDialog;
    setApproveMessagesDialog((prev) => ({
      ...prev,
      messageIndex: prev.messageIndex === 0 ? messages.length - 1 : prev.messageIndex - 1,
      messageToEditText: null,
      messageToEditIndex: null,
    }));
  };

  const handleEditMessage = (idx: number) => {
    return () => {
      const message = approveMessagesDialog.messages[idx];
      if (_.isNil(message)) {
        console.warn(`handleEditMessage: Tried to access message at index ${idx}, but it was undefined.`, approveMessagesDialog.messages);
        return;
      }
      setApproveMessagesDialog((prev) => ({ ...prev, messageToEditText: message.text, messageToEditIndex: idx }));
    };
  };

  const handleCancelEditMessage = () => {
    setApproveMessagesDialog((prev) => ({ ...prev, messageToEditText: null, messageToEditIndex: null }));
  };

  const handleMessageToApproveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApproveMessagesDialog((prev) => ({ ...prev, messageToEditText: event.target.value }));
  };

  const handleSaveAndApproveMessage = async () => {
    const { funnelId, messages, messageToEditText, messageToEditIndex } = approveMessagesDialog;

    if (!_.isNil(funnelId) && !_.isNil(messageToEditText) && !_.isNil(messageToEditIndex)) {
      const updateMessages = [...messages];

      const messageToApprove = updateMessages[messageToEditIndex];
      if (_.isNil(messageToApprove)) {
        console.warn('handleSaveAndApproveMessage: messageToApprove is undefined', { messageToEditIndex, updateMessages });
        return;
      }
      messageToApprove.approved = true;
      messageToApprove.text = messageToEditText;

      setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));

      const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

      const res = await updateToBeApprovedMessages(funnelId, [messageToApprove]);

      if (res.error) {
        setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
        if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
          handleOpenCopilotLicenseExpiredDialog();
        } else {
          handleOpenErrorDialog(res.error, '/messages/bulk-approve', 'post');
        }
      } else {
        setApproveMessagesDialog((prev) => ({
          ...prev,
          loading: false,
          messageIndex: 0,
          messages: prev.messages.filter((message) => _.isNil(message.approved)),
        }));
        if (messagesToApprove.length === 0) {
          setFunnels((prevFunnels) => prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: 0 } : funnel)));
          handleCloseApproveMessagesDialog();
          handleOpenAllMessagesProcessedSnackbar();
        } else {
          handleOpenApproveMessageSuccessSnackbar();
          setFunnels((prevFunnels) =>
            prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: funnel.toBeApprovedMessages - 1 } : funnel))
          );
        }
      }
    }
  };

  const handleApproveMessage = (idx: number) => {
    return async () => {
      const { messages, funnelId } = approveMessagesDialog;

      if (!_.isNil(funnelId)) {
        const updateMessages = [...messages];

        const messageToApprove = updateMessages[idx];
        if (_.isNil(messageToApprove)) {
          console.warn('handleApproveMessage: messageToApprove is undefined', { idx, updateMessages });
          return;
        }
        messageToApprove.approved = true;

        setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));

        const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

        const res = await updateToBeApprovedMessages(funnelId, [messageToApprove]);

        if (res.error) {
          setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
          if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
            handleOpenCopilotLicenseExpiredDialog();
          } else {
            handleOpenErrorDialog(res.error, '/messages/bulk-approve', 'post');
          }
        } else {
          setApproveMessagesDialog((prev) => ({
            ...prev,
            loading: false,
            messageIndex: 0,
            messages: prev.messages.filter((message) => _.isNil(message.approved)),
          }));
          if (messagesToApprove.length === 0) {
            setFunnels((prevFunnels) => prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: 0 } : funnel)));
            handleCloseApproveMessagesDialog();
            handleOpenAllMessagesProcessedSnackbar();
          } else {
            setFunnels((prevFunnels) =>
              prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: funnel.toBeApprovedMessages - 1 } : funnel))
            );
            handleOpenApproveMessageSuccessSnackbar();
          }
        }
      }
    };
  };

  const handleCloseApproveMessagesDialog = async () => {
    // Aufgaben-Zähler in der Navigation nach Freigaben sofort aktualisieren.
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    setApproveMessagesDialog({
      open: false,
      loading: false,
      funnelId: null,
      messages: [],
      messageIndex: 0,
      messageSender: null,
      messageToEditText: null,
      messageToEditIndex: null,
    });
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['taskCounts'] });
  };

  /**
   * Confirm message cancellation dialog handlers
   */

  const handleOpenConfirmDeclineMessageDialog = () => {
    setConfirmDeclineMessageDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseConfirmDeclineMessageDialog = () => {
    setConfirmDeclineMessageDialog({ open: false, loading: false });
  };

  const handleDenyMessage = (deactivateContact: boolean = false) => {
    return async () => {
      const { messages, funnelId, messageIndex } = approveMessagesDialog;

      if (!_.isNil(funnelId)) {
        const updateMessages = [...messages];

        const messageToApprove = updateMessages[messageIndex];
        messageToApprove.approved = false;

        setApproveMessagesDialog((prev) => ({ ...prev, loading: true, messageToEditText: null, messageToEditIndex: null, messages: updateMessages }));
        setConfirmDeclineMessageDialog((prev) => ({ ...prev, loading: true }));

        const messagesToApprove = updateMessages.filter((message) => _.isNil(message.approved));

        const res = await updateToBeApprovedMessages(funnelId, [messageToApprove]);

        if (res.error) {
          if (res.error.message === ErrorMessages.CO_PILOT_LICENSE_EXPIRED || res.error.message === ErrorMessages.NO_CO_PILOT_LICENSE_FOUND) {
            handleOpenCopilotLicenseExpiredDialog();
          } else {
            handleOpenErrorDialog(res.error, '/messages/bulk-approve', 'post');
          }
          setApproveMessagesDialog((prev) => ({ ...prev, loading: false }));
          setConfirmDeclineMessageDialog((prev) => ({ ...prev, loading: false }));
        } else {
          if (deactivateContact) {
            const url = messageToApprove.linkedInURL;

            if (url) {
              const resDeactivate = await updateContactIsActive(funnelId, [url], false);

              if (resDeactivate.error) {
                handleOpenErrorDialog(resDeactivate?.error, '/campaigns/:id/contacts', 'put');
              }
            }
          }
          setApproveMessagesDialog((prev) => ({
            ...prev,
            loading: false,
            messageIndex: 0,
            messages: prev.messages.filter((message) => _.isNil(message.approved)),
          }));
          if (messagesToApprove.length === 0) {
            setFunnels((prevFunnels) => prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: 0 } : funnel)));
            handleCloseApproveMessagesDialog();
            handleOpenAllMessagesProcessedSnackbar();
          } else {
            setFunnels((prevFunnels) =>
              prevFunnels.map((funnel) => (funnel._id === funnelId ? { ...funnel, toBeApprovedMessages: funnel.toBeApprovedMessages - 1 } : funnel))
            );
          }

          handleCloseConfirmDeclineMessageDialog();
        }
      }
    };
  };

  /**
   * Edit LinkedIn account and restart campaign handlers
   */

  const handleOpenEditCampaignLinkedinAccountDialog = (funnel: FunnelOverview) => {
    return () => {
      setEditCampaignLinkedinAccountDialog((prev) => ({ ...prev, open: true, funnel }));
    };
  };

  const handleCloseEditCampaignLinkedinAccountDialog = () => {
    setEditCampaignLinkedinAccountDialog({ open: false, loading: false, funnel: null, selectedLinkedinAccount: '' });
  };

  const handleCampaignLinkedinAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditCampaignLinkedinAccountDialog((prev) => ({ ...prev, open: true, selectedLinkedinAccount: event.target.value }));
  };

  const handleAcceptAccountAndRestartCampaign = async () => {
    const { funnel, selectedLinkedinAccount } = editCampaignLinkedinAccountDialog;

    if (_.isNil(funnel)) return;

    setEditCampaignLinkedinAccountDialog((prev) => ({ ...prev, loading: true }));

    const updateRes = await updateFunnel(funnel._id, { linkedinAccount: selectedLinkedinAccount });

    if (!updateRes.error) {
      handleCloseEditCampaignLinkedinAccountDialog();
      handleRestartFunnel(funnel._id)();
    } else {
      setEditCampaignLinkedinAccountDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(updateRes?.error, '/campaigns/:id', 'patch');
    }
  };

  const riskReductionForAccount = (liAccountId: string) => {
    const account = linkedinAccounts.find((a) => a._id === liAccountId);
    const until = activeRiskReductionUntil(account);
    return until ? { until, percent: riskReductionPercent(account) } : null;
  };

  const mapLinkedinAccountIdToName = (liAccountId: string) => {
    const liAccount = linkedinAccounts.find((account) => account._id === liAccountId);

    if (liAccount) {
      return !_.isNil(liAccount.firstName) && !_.isNil(liAccount.lastName) ? `${liAccount.firstName} ${liAccount.lastName}` : liAccount.userEmail;
    }

    return '-';
  };

  /**
   * Campaign status descriptions dialog handlers
   */

  const handleOpenCampaignStatusDescriptionsDialog = () => {
    setCampaignStatusDescriptionsDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseCampaignStatusDescriptionsDialog = () => {
    setCampaignStatusDescriptionsDialog({ open: false });
  };

  /**
   * Work Units Status Modal handlers
   */

  const handleOpenWorkUnitsStatusModal = () => {
    setWorkUnitsStatusModal({ open: true });
  };

  const handleCloseWorkUnitsStatusModal = () => {
    setWorkUnitsStatusModal({ open: false });
  };

  /**
   * Success snackbar handlers
   */

  const handleOpenApproveMessageSuccessSnackbar = () => {
    setApproveMessageSuccess(true);
  };

  const handleCloseApproveMessageSuccessSnackbar = () => {
    setApproveMessageSuccess(false);
  };

  const handleOpenAllMessagesProcessedSnackbar = () => {
    setAllMessagesProcessed(true);
  };

  const handleCloseAllMessagesProcessedSnackbar = () => {
    setAllMessagesProcessed(false);
  };

  /**
   * Campaign selection handlers
   */

  const handleCampaignSelection = (event: React.MouseEvent, campaignId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedCampaignId(campaignId);
  };

  /**
   * Campaign detail view handlers
   */

  const handleOpenScheduleDialog = (funnel: FunnelOverview) => () => {
    setScheduleDialog({ open: true, funnel });
  };

  const handleCancelSchedule = (funnel: FunnelOverview) => async () => {
    try {
      await cancelCampaignSchedule(funnel._id);
      setFunnels((prev) => prev.map((f) => (f._id === funnel._id ? { ...f, scheduledStartAt: undefined } : f)));
    } catch (e) {
      console.error('Cancel schedule failed', e);
    }
  };

  const handleScheduled = (scheduledStartAt: string) => {
    const funnelId = scheduleDialog.funnel?._id;
    if (!funnelId) return;
    setFunnels((prev) => prev.map((f) => (f._id === funnelId ? { ...f, scheduledStartAt } : f)));
  };

  /**
   * The detail view is a real route, so opening/closing it is navigation. The
   * `preSelect` argument survives only as a query param for older entry points.
   */
  const handleOpenCampaignDetailView = (funnel: FunnelOverview, preSelect: StatisticsAccordionStates | null) => {
    if (!funnel?._id) {
      return;
    }
    const suffix = preSelect ? `?section=${preSelect}` : '';
    navigate(`${campaignListPath}/${funnel._id}${suffix}`);
  };

  const handleCloseCampaignDetailView = () => {
    navigate(campaignListPath);
  };

  /**
   * Edit funnel handlers
   */

  const handleEditFunnel = (funnelId: string) => {
    return () => {
      // Recruiting: Entwürfe, die im geführten Flow „Neue Kampagne“ entstanden sind, werden dort vervollständigt.
      // Alte, von Hand gebaute Entwürfe bleiben im Builder (der Assistent würde ihren Ablauf ersetzen).
      const draft = funnels.find((f) => f._id === funnelId);
      if (editsInWizard(productGroup === ProductGroups.HR_BOOSTER, draft?.status === FunnelStatuses.CREATED, draft)) {
        navigate(`${Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN}/${funnelId}`);
        return;
      }
      const campaignStepsArray = _.map(CampaignBuilderSteps, (tab) => tab);
      const selectedIndex = campaignStepsArray.findIndex((step) => step === CampaignBuilderSteps.CAMPAIGN_SUMMARY);

      if (currentActiveFunnelId !== funnelId) {
        resetCampaignBuilder();
        navigate({
          pathname: campaignRoute,
          search: `?_id=${funnelId}&stepIndex=${selectedIndex}`,
        });
      } else {
        navigate({
          pathname: campaignRoute,
          search: `?_id=${funnelId}&stepIndex=${selectedIndex}`,
        });
      }
      autoSizePanel(selectedIndex);
      fetchFinalContacts(undefined, undefined, funnelId);
    };
  };

  const handleOpenSaveGlobalFunnelDialog = (funnel: FunnelOverview) => {
    setSaveGlobalFunnelDialog({ open: true, funnel });
    setGlobalFunnelForm({ description: '', conversionRate: '', creatorName: '' });
    setGlobalFunnelError('');
  };

  const handleCloseSaveGlobalFunnelDialog = () => {
    setSaveGlobalFunnelDialog({ open: false, funnel: null });
    setGlobalFunnelForm({ description: '', conversionRate: '', creatorName: '' });
    setGlobalFunnelError('');
  };

  const handleGlobalFunnelFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFunnelForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const handleSaveGlobalFunnel = async () => {
    const { funnel } = saveGlobalFunnelDialog;
    if (_.isNil(funnel)) return;
    setGlobalFunnelLoading(true);
    setGlobalFunnelError('');
    try {
      await addFunnelToGlobalStore(funnel._id, {
        description: globalFunnelForm.description,
        conversionRate: parseFloat(globalFunnelForm.conversionRate),
        creatorName: globalFunnelForm.creatorName,
      });
      setGlobalFunnelSuccess(true);
      handleCloseSaveGlobalFunnelDialog();
    } catch (error: any) {
      setGlobalFunnelError(error?.response?.data?.message || t('globalFunnelSaveError'));
    } finally {
      setGlobalFunnelLoading(false);
    }
  };

  return (
    <>
      <div className={classes.container}>
        {routeCampaignId ? (
          <CampaignDetailPage
            campaignId={routeCampaignId}
            onBack={handleCloseCampaignDetailView}
            onEdit={handleEditFunnel(routeCampaignId)}
            mapLinkedinAccountIdToName={mapLinkedinAccountIdToName}
            productGroup={productGroup}
          />
        ) : (
          <>
            <div className={classes.tabContainer}>
              <Stack direction={'row'} alignItems={'flex-end'} justifyContent={'space-between'} flexWrap={'wrap'} gap={3}>
                <Stack gap={'6px'}>
                  <Typography className={classes.pageTitle}>{t('campaignAlt')}</Typography>
                  <Typography className={classes.pageSubtitle}>
                    {t('campaignCountLine', {
                      shown: funnels?.length ?? 0,
                      total: itemCount,
                      active: activeCampaignCount,
                    })}
                  </Typography>
                </Stack>
                <Stack direction={'row'} alignItems={'center'} gap={'12px'}>
                  <PaiTooltip title={t('workUnitsStatus')}>
                    <Button className={classes.secondaryAction} startIcon={<AssessmentIcon />} onClick={handleOpenWorkUnitsStatusModal}>
                      {t('workUnits')}
                    </Button>
                  </PaiTooltip>
                  <PaiButton
                    text={t('newCampaignShort')}
                    border={'rounded'}
                    action={handleNewFunnelClick}
                    disabled={!productLicensed && funnels.length >= 1}
                    style={{ height: '40px', padding: '0 22px', borderRadius: '8px', fontSize: '0.9375rem', fontWeight: 700 }}
                  />
                </Stack>
              </Stack>

              <Stack className={classes.filterBar} direction={'row'} alignItems={'flex-end'} flexWrap={'wrap'} gap={'12px'}>
                <PaiTextField
                  value={searchParam}
                  label={t('campaignName')}
                  onChange={handleSearchParamChange}
                  onKeyDown={searchKeyDown}
                  onKeyUp={searchKeyUp}
                  placeholder={t('searchCampaignPlaceholder')}
                  style={{ width: '420px' }}
                />
                <PaiSelect
                  value={linkedinAccountFilter}
                  label={t('linkedinAccount')}
                  multiple={true}
                  style={{ width: '220px' }}
                  onChange={handleLinkedinAccountFilterChange}
                >
                  <PaiMenuItem value={''} disabled={true}>
                    {t('selectLinkedinAccount')}
                  </PaiMenuItem>
                  {linkedinAccountOptions.map((option) => (
                    <PaiMenuItem key={option.value} value={option.value} isActive={linkedinAccountFilter.includes(option.value)}>
                      {option.label}
                    </PaiMenuItem>
                  ))}
                </PaiSelect>
                <PaiSelect
                  value={campaignStatusFilter}
                  label={t('status')}
                  multiple={true}
                  style={{ width: '220px' }}
                  onChange={handleCampaignStatusFilterChange}
                >
                  <PaiMenuItem value={''} disabled={true}>
                    {t('selectTheCampaignStatus')}
                  </PaiMenuItem>
                  {funnelStatusOptions.map((option) => (
                    <PaiMenuItem key={option.value} value={option.value} isActive={campaignStatusFilter.includes(option.value)}>
                      {option.label}
                    </PaiMenuItem>
                  ))}
                </PaiSelect>
                {!isAdminLogin ? (
                  <PaiTooltip title={t('rememberLinkedinAccount')}>
                    <Box className={classes.rememberAccounts}>
                      <PaiCheckbox
                        checked={rememberLinkedinAccount}
                        onChange={handleRememberLinkedinAccount}
                        disabled={isAdminLogin || _.isEmpty(linkedinAccountFilter)}
                      />
                      <Typography className={classes.rememberAccountsLabel}>{t('rememberAccounts')}</Typography>
                    </Box>
                  </PaiTooltip>
                ) : null}
                <ToggleButtonGroup
                  className={classes.viewToggle}
                  size={'small'}
                  exclusive
                  value={viewMode}
                  onChange={(_e, value) => {
                    if (value) {
                      setViewMode(value);
                      localStorage.setItem('campaignViewMode', value);
                    }
                  }}
                >
                  <ToggleButton value={'list'} aria-label={'list view'}>
                    <ViewListIcon fontSize={'small'} />
                  </ToggleButton>
                  <ToggleButton value={'board'} aria-label={'kanban view'}>
                    <ViewKanbanIcon fontSize={'small'} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </div>
            <Stack className={classes.outerContent} direction={'row'} gap={2}>
              <div className={classes.content}>
                {fetchFunnelsLoading ? (
                  <PaiDotLoader />
                ) : viewMode === 'board' ? (
                  <CampaignKanban
                    funnels={funnels}
                    mapLinkedinAccountIdToName={mapLinkedinAccountIdToName}
                    riskReductionForAccount={riskReductionForAccount}
                    onOpenDetail={(funnel) => handleOpenCampaignDetailView(funnel, null)}
                  />
                ) : (
                  <div className={classes.listWrapper}>
                    <div className={classes.columnHeader}>
                      <div className={cx(classes.columnHeaderCell, classes.colCampaign)}>{t('campaignAlt')}</div>
                      <Stack className={cx(classes.columnHeaderCell, classes.colStatus)} direction={'row'} alignItems={'center'} gap={'4px'}>
                        {t('campaignStatus')}
                        <IconButton className={classes.helpBtn} onClick={handleOpenCampaignStatusDescriptionsDialog}>
                          <HelpOutlineIcon className={classes.helpIcon} />
                        </IconButton>
                      </Stack>
                      <div className={cx(classes.columnHeaderCell, classes.colMetric)}>{getLocalizedText('contacts', productGroup, t)}</div>
                      <div className={cx(classes.columnHeaderCell, classes.colMetric)}>{t('contacted')}</div>
                      <div className={cx(classes.columnHeaderCell, classes.colReplies)}>{t('campaignAcceptedReplied')}</div>
                      <div className={classes.colActions} />
                    </div>

                    <div className={classes.cardList}>
                      {!_.isEmpty(funnels) ? (
                        funnels.map((funnel) => (
                          <CampaignItem
                            key={funnel._id}
                            funnel={funnel}
                            startFunnelLoading={startFunnelLoading}
                            isSelected={funnel._id === selectedCampaignId}
                            isNew={funnel._id === newCampaignId}
                            handleOpenApproveMessagesDialog={handleOpenApproveMessagesDialog}
                            handleOpenApproveContactsDialog={handleOpenApproveContactsDialog}
                            handleOpenConfirmDeleteDialog={handleOpenConfirmDeleteDialog}
                            handleOpenRenameCampaignDialog={handleOpenRenameCampaignDialog}
                            handleOpenScheduleDialog={handleOpenScheduleDialog}
                            handleCancelSchedule={handleCancelSchedule}
                            handleOpenCloneCampaignDialog={handleOpenCloneCampaignDialog}
                            handleStopFunnel={handleStopFunnel}
                            handleEditFunnel={handleEditFunnel}
                            handleRestartFunnel={handleRestartFunnel}
                            handleArchiveCampaign={handleArchiveCampaign}
                            handleOpenEditCampaignLinkedinAccountDialog={handleOpenEditCampaignLinkedinAccountDialog}
                            mapLinkedinAccountIdToName={mapLinkedinAccountIdToName}
                            riskReductionForAccount={riskReductionForAccount}
                            handleCampaignSelection={handleCampaignSelection}
                            handleOpenCampaignDetailView={handleOpenCampaignDetailView}
                            userRole={role}
                            onSaveToGlobalStore={handleOpenSaveGlobalFunnelDialog}
                          />
                        ))
                      ) : (
                        <div className={classes.emptyState}>{t('noCampaignsFound')}</div>
                      )}
                    </div>

                    <Stack direction={'row'} justifyContent={'flex-end'}>
                      <PaiPagination
                        total={itemCount}
                        itemsPerPage={itemsPerPage}
                        page={page}
                        hidden={funnels?.length === 0 || itemCount <= itemsPerPage}
                        handleChangePage={handleChangePage}
                      />
                    </Stack>
                  </div>
                )}
              </div>
              {scheduleDialog.funnel && (
                <ScheduleStartDialog
                  open={scheduleDialog.open}
                  campaignId={scheduleDialog.funnel._id}
                  campaignName={scheduleDialog.funnel.name}
                  linkedinAccountId={scheduleDialog.funnel.linkedinAccount}
                  currentScheduledStartAt={scheduleDialog.funnel.scheduledStartAt}
                  onClose={() => setScheduleDialog({ open: false, funnel: null })}
                  onScheduled={handleScheduled}
                />
              )}
            </Stack>
          </>
        )}
      </div>

      <DeleteCampaignDialog
        open={confirmDeleteDialog.open}
        loading={confirmDeleteDialog.loading}
        funnel={confirmDeleteDialog.funnel}
        handleClose={handleCloseConfirmDeleteDialog}
        handleDeleteFunnel={handleDeleteFunnel}
      />
      <CloneCampaignDialog
        open={cloneCampaignDialog.open}
        loading={cloneCampaignDialog.loading}
        nameTakenError={cloneCampaignDialog.nameTakenError}
        funnel={cloneCampaignDialog.funnel}
        funnelName={cloneCampaignDialog.funnelName}
        linkedinAccounts={linkedinAccounts}
        selectedLinkedinAccount={cloneCampaignDialog.selectedLinkedinAccount}
        handleFunnelNameChange={handleCloneCampaignNameChange}
        handleLinkedinAccountChange={handleLinkedinAccountChange}
        handleCloneContactsChange={handleCloneContactsChange}
        handleClose={handleCloseCloneCampaignDialog}
        acceptFunnelName={handleCloneCampaign}
      />
      <RenameCampaignDialog
        open={renameCampaignDialog.open}
        loading={renameCampaignDialog.loading}
        nameTakenError={renameCampaignDialog.nameTakenError}
        campaignName={renameCampaignDialog.campaignName}
        handleCampaignNameChange={handleCampaignNameChange}
        handleClose={handleCloseRenameCampaignDialog}
        handleRenameCampaign={handleRenameCampaign}
      />
      <ApproveContactsDialog
        open={!_.isNil(approveContactsFunnelId)}
        funnelId={approveContactsFunnelId}
        handleClose={() => setApproveContactsFunnelId(null)}
        handleRemainingChange={handleContactApprovalRemainingChange}
        handleOpenContactDrawer={handleOpenContactDrawer}
      />
      <ApproveMessagesDialog
        open={approveMessagesDialog.open}
        loading={approveMessagesDialog.loading}
        messages={approveMessagesDialog.messages}
        messageIndex={approveMessagesDialog.messageIndex}
        messageSender={approveMessagesDialog.messageSender}
        messageToEditText={approveMessagesDialog.messageToEditText}
        messageToEditIndex={approveMessagesDialog.messageToEditIndex}
        handleNextMessage={handleNextMessage}
        handlePrevMessage={handlePrevMessage}
        handleRemoveContact={handleRemoveContact}
        handleEditMessage={handleEditMessage}
        handleCancelEditMessage={handleCancelEditMessage}
        handleMessageToApproveChange={handleMessageToApproveChange}
        handleSaveAndApproveMessage={handleSaveAndApproveMessage}
        handleApproveMessage={handleApproveMessage}
        handleOpenConfirmDeclineMessageDialog={handleOpenConfirmDeclineMessageDialog}
        handleClose={handleCloseApproveMessagesDialog}
        handleOpenContactDrawer={handleOpenContactDrawer}
      />
      <ConfirmDeclineMessageDialog
        open={confirmDeclineMessageDialog.open}
        loading={confirmDeclineMessageDialog.loading}
        handleClose={handleCloseConfirmDeclineMessageDialog}
        handleDenyMessage={handleDenyMessage()}
        handleDenyMessageAndDeactivateContact={handleDenyMessage(true)}
      />
      <EditCampaignLinkedinAccountDialog
        open={editCampaignLinkedinAccountDialog.open}
        loading={editCampaignLinkedinAccountDialog.loading}
        linkedinAccounts={linkedinAccounts}
        selectedLinkedinAccount={editCampaignLinkedinAccountDialog.selectedLinkedinAccount}
        handleCampaignLinkedinAccountChange={handleCampaignLinkedinAccountChange}
        handleClose={handleCloseEditCampaignLinkedinAccountDialog}
        handleAcceptAccountAndRestartCampaign={handleAcceptAccountAndRestartCampaign}
      />
      <CampaignStatusDescriptionsDialog open={campaignStatusDescriptionsDialog.open} handleClose={handleCloseCampaignStatusDescriptionsDialog} />
      <WorkUnitsStatusModal open={workUnitsStatusModal.open} handleClose={handleCloseWorkUnitsStatusModal} />
      <PaiSuccessAlert
        open={approveMessageSuccess}
        message={t('approveMessageSuccessMessage')}
        position={'bottom'}
        handleClose={handleCloseApproveMessageSuccessSnackbar}
      />
      <PaiSuccessAlert
        open={allMessagesProcessed}
        message={t('allMessagesProcessed')}
        position={'bottom'}
        handleClose={handleCloseAllMessagesProcessedSnackbar}
      />
      <ContactDrawer
        open={contactDrawer.open}
        productGroup={productGroup}
        funnelId={contactDrawer.funnelId}
        contactIndex={contactDrawer.contactIndex}
        profileId={contactDrawer.profileId}
        aiJudgement={contactDrawer.aiJudgement}
        aiJudgementExplanation={contactDrawer.aiJudgementExplanation}
        assessment={contactDrawer.assessment}
        percentiles={contactDrawer.percentiles}
        isActive={contactDrawer.isActive}
        contactDetails={contactDrawer.contactDetails}
        aboutContact={contactDrawer.aboutContact}
        aboutCompany={contactDrawer.aboutCompany}
        handleUpdateDrawerContactIsActive={handleUpdateDrawerContactIsActive}
        handleCloseDrawer={handleCloseContactDrawer}
      />
      {/* Save to Global Store Modal */}
      <Dialog open={saveGlobalFunnelDialog.open} onClose={handleCloseSaveGlobalFunnelDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('saveToGlobalStore')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="description"
            label={t('globalFunnelDescription')}
            type="text"
            fullWidth
            value={globalFunnelForm.description}
            onChange={handleGlobalFunnelFormChange}
            disabled={globalFunnelLoading}
          />
          <TextField
            margin="dense"
            name="conversionRate"
            label={t('globalFunnelConversionRate')}
            type="number"
            fullWidth
            value={globalFunnelForm.conversionRate}
            onChange={handleGlobalFunnelFormChange}
            disabled={globalFunnelLoading}
            inputProps={{ min: 0, max: 1, step: 0.01 }}
          />
          <TextField
            margin="dense"
            name="creatorName"
            label={t('globalFunnelCreatorName')}
            type="text"
            fullWidth
            value={globalFunnelForm.creatorName}
            onChange={handleGlobalFunnelFormChange}
            disabled={globalFunnelLoading}
          />
          {globalFunnelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {globalFunnelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveGlobalFunnelDialog} disabled={globalFunnelLoading}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSaveGlobalFunnel}
            disabled={globalFunnelLoading || !globalFunnelForm.description || !globalFunnelForm.creatorName || !globalFunnelForm.conversionRate}
            variant="contained"
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={globalFunnelSuccess}
        autoHideDuration={4000}
        onClose={() => setGlobalFunnelSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setGlobalFunnelSuccess(false)} severity="success" sx={{ width: '100%' }}>
          {t('globalFunnelSaveSuccess')}
        </Alert>
      </Snackbar>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  tabContainer: {
    padding: '28px 40px 20px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  pageTitle: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
  },
  pageSubtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  secondaryAction: {
    height: '40px',
    padding: '0 14px',
    minWidth: 'auto',
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: '8px',
    background: '#FFF',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    textTransform: 'none',
    color: theme.palette.greys.dark,
    whiteSpace: 'nowrap',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(18),
    },
    '&:hover': {
      background: '#FFF',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  filterBar: {
    /** The view switch is pushed to the trailing edge, everything else stays left. */
    '& > :last-child': {
      marginLeft: 'auto',
    },
  },
  rememberAccounts: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    height: '40px',
  },
  rememberAccountsLabel: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
  },
  viewToggle: {
    height: '40px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: '8px',
    background: '#FFF',
    overflow: 'hidden',
    '& .MuiToggleButton-root': {
      width: '40px',
      height: '40px',
      border: 'none',
      borderRadius: 0,
      color: theme.palette.greys.medium,
      '&:not(:first-of-type)': {
        borderLeft: `1px solid ${theme.palette.greys.lighter}`,
      },
      '&:hover': {
        color: theme.palette.primary.main,
      },
      '&.Mui-selected': {
        background: theme.palette.backgrounds.main,
        color: theme.palette.primary.main,
        '&:hover': {
          background: theme.palette.backgrounds.main,
        },
      },
      '& .MuiSvgIcon-root': {
        fontSize: theme.typography.pxToRem(20),
      },
    },
  },
  outerContent: {
    height: 'calc(100% - 100px)',
    padding: '0 40px 40px 40px',
  },
  content: {
    height: '100%',
    flex: 1,
    overflow: 'auto',
    display: 'flex',
  },
  listWrapper: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '0 24px',
  },
  columnHeaderCell: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
  },
  /** Column widths mirror the card columns in campaign-item.tsx. */
  colCampaign: {
    flex: 1,
    minWidth: 0,
  },
  colStatus: {
    width: '200px',
    flexShrink: 0,
  },
  colMetric: {
    width: '120px',
    flexShrink: 0,
    textAlign: 'right',
  },
  colReplies: {
    width: '150px',
    flexShrink: 0,
    textAlign: 'right',
  },
  colActions: {
    width: '132px',
    flexShrink: 0,
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  emptyState: {
    background: '#FFF',
    borderRadius: '12px',
    padding: '32px 24px',
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
    boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)',
  },
  helpBtn: {
    padding: '2px',
    color: theme.palette.greys.medium,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  helpIcon: {
    fontSize: theme.typography.pxToRem(18),
  },
}));

export default CampaignOverview;
